import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';

interface Modifier {
  modifierId: string;
  name: string;
  [key: string]: any;
}

interface Product {
  productId: string;
  [key: string]: any;
}

interface Menu {
  modifiers?: Modifier[];
  [key: string]: any;
}

interface Slot {
  slotId: string;
  modifiers?: any[];
  [key: string]: any;
}

export interface ModifierSelectProps {
  value: Modifier | null | Modifier[];
  onChange: (modifier: Modifier | null | Modifier[]) => void;
  product?: Product | null;
  menu?: Menu | null;
  slot?: Slot | null;
  multiple?: boolean;
}

export function ModifierSelect({ 
  value, 
  onChange, 
  product,
  menu,
  slot,
  multiple = false 
}: ModifierSelectProps) {
  const [modifiers, setModifiers] = useState<Modifier[]>([]);
  const [modifiersLoading, setModifiersLoading] = useState(false);

  useEffect(() => {
    setModifiersLoading(true);
    
    // If a specific slot is provided, only show modifiers from that slot
    if (slot && slot.modifiers && Array.isArray(slot.modifiers)) {
      const slotModifiers = slot.modifiers
        .map((modifierItem: any) => modifierItem.modifier)
        .filter(Boolean);
      console.log('ModifierSelect - Using modifiers from specific slot:', slotModifiers.length, 'modifiers');
      setModifiers(slotModifiers);
      setModifiersLoading(false);
      return;
    }
    
    // If no product is provided, try to get all modifiers from the menu
    if (!product && menu && menu.modifiers && Array.isArray(menu.modifiers)) {
      console.log('ModifierSelect - Using all modifiers from menu:', menu.modifiers.length, 'modifiers');
      setModifiers(menu.modifiers);
      setModifiersLoading(false);
      return;
    }
    
    // Extract modifiers from product's variants
    if (product && product.variants && Array.isArray(product.variants)) {
      // Use the first variant by default, or check if there's a selected variant in product
      const selectedVariant = product.selectedVariant || product.variants[0];
      
      if (selectedVariant && selectedVariant.slots && Array.isArray(selectedVariant.slots)) {
        // Extract modifiers from all slots
        const allModifiers: Modifier[] = [];
        
        selectedVariant.slots.forEach((slotItem: any) => {
          if (slotItem.modifiers && Array.isArray(slotItem.modifiers)) {
            slotItem.modifiers.forEach((modifierItem: any) => {
              if (modifierItem.modifier) {
                allModifiers.push(modifierItem.modifier);
              }
            });
          }
        });
        
        // Deduplicate modifiers by modifierId to prevent duplicate keys in React
        const uniqueModifiers = allModifiers.filter((modifier, index, array) => 
          array.findIndex(m => m.modifierId === modifier.modifierId) === index
        );
        
        // Debug logging for duplicate modifier detection
        if (allModifiers.length !== uniqueModifiers.length) {
          console.log(`ModifierSelect - Removed ${allModifiers.length - uniqueModifiers.length} duplicate modifiers`);
          console.log('ModifierSelect - Extracted from product.variants[].slots[].modifiers:', 
            uniqueModifiers.length, 'modifiers', 
            `(${allModifiers.length})`, 
            uniqueModifiers.map(m => m.name || m.modifierCode || m.modifierId)
          );
        } else {
          console.log('ModifierSelect - Extracted from product.variants[].slots[].modifiers:', 
            uniqueModifiers.length, 'modifiers', 
            `(${allModifiers.length})`, 
            uniqueModifiers.map(m => m.name || m.modifierCode || m.modifierId)
          );
        }
        
        setModifiers(uniqueModifiers);
      } else {
        console.log('ModifierSelect - No slots found in selected variant:', selectedVariant);
        setModifiers([]);
      }
    } else {
      console.log('ModifierSelect - No variants available in product:', product);
      setModifiers([]);
    }
    
    setModifiersLoading(false);
  }, [product, menu, slot]);

  return (
    <Autocomplete
      options={Array.isArray(modifiers) ? modifiers : []}
      disableCloseOnSelect
      filterSelectedOptions
      getOptionLabel={(option: Modifier) => {
        if (!option) return 'No modifier';
        if (!option.name && !option.modifierCode) return 'Unknown';
        return `${option.name ?? 'Unknown'} (${option.modifierCode ?? 'Unknown'})`;
      }}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        return option.modifierId === (val as Modifier).modifierId;
      }}
      renderOption={(props, option) => {
        if (!option) return null;
        const label = (!option.name && !option.modifierCode)
          ? 'Unknown'
          : `${option.name ?? 'Unknown'} (${option.modifierCode ?? 'Unknown'})`;
        return (
          <li {...props} key={option.modifierId || `modifier-${Math.random()}`}>
            {label}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        if (!tagValue || !Array.isArray(tagValue)) return [];
        return (tagValue as Modifier[]).map((option, index) => {
          if (!option) return null;
          const label = (!option.name && !option.modifierCode)
            ? 'Unknown'
            : `${option.name ?? 'Unknown'} (${option.modifierCode ?? 'Unknown'})`;
          return (
            <Chip
              {...getTagProps({ index })}
              key={option.modifierId || `modifier-tag-${index}`}
              size="small"
              label={label}
            />
          );
        });
      }}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      multiple={multiple}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Modifier"
          placeholder="Search for modifiers..."
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {modifiersLoading && <CircularProgress data-testid="loading-indicator" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
